import React, { createContext, useContext } from 'react';
import { IWedding_Website } from '@bridebook/models/source/models/Weddings.types';
import { DEFAULT_THEME } from '@bridebook/web/lib/wedding-website/constants/themes';
import { getWebsiteTheme } from '@bridebook/web/lib/weddings/selectors';
import { useSelector } from '@bridebook/web/lib/utils';

export interface IWebsiteDesignContext {
  theme: IWedding_Website['config']['theme'];
  setPalette: (palette: string) => void;
  setFont: (font: string) => void;
  // theme is determined by using the getThemeId util
  setThemeById: (themeId: string) => void;
  // theme is passed as an object as in the model
  setTheme: (theme: IWedding_Website['config']['theme']) => void;
}

const WebsiteDesignContext = createContext<IWebsiteDesignContext | undefined>(undefined);

interface IWebsiteDesignProviderProps extends React.PropsWithChildren {
  value: IWebsiteDesignContext;
}

export const WebsiteDesignProvider = ({ children, value }: IWebsiteDesignProviderProps) => (
  <WebsiteDesignContext.Provider value={value}>{children}</WebsiteDesignContext.Provider>
);

export const useWebsiteDesign = () => {
  const context = useContext(WebsiteDesignContext);
  if (!context) {
    throw new Error('useWebsiteDesignContext must be used within a WebsiteThemeContextProvider');
  }
  return context;
};

/**
 * Hook to safely access website theme data.
 * This hook allows using a single hook inside the Wedding Website on both mobile and desktop.
 * It returns the theme from context if available, otherwise falls back to the stored theme from Redux.
 */

export const useWebsiteThemeSafe = () => {
  const context = useContext(WebsiteDesignContext);
  const storedTheme = useSelector(getWebsiteTheme) ?? DEFAULT_THEME;

  if (!context) {
    return storedTheme;
  }
  return {
    palette: context.theme.palette,
    font: context.theme.font,
  };
};
